import React, { useState } from 'react'
import { api } from '../lib/api'

/**
 * ProfileContactModal - opened from the 3-day WhatsApp banner on /subjects.
 * Optional phone + whatsapp opt-in. The student can dismiss the banner and
 * re-open this modal later from their profile. Validates Mozambican E.164
 * phone format (+258 + 9 digits).
 */

interface Props {
  initialPhone?: string | null
  initialOptedIn?: boolean
  onClose: () => void
  onSaved: () => void
}

export function ProfileContactModal({ initialPhone, initialOptedIn, onClose, onSaved }: Props) {
  const [phone, setPhone]       = useState(initialPhone ?? '+258')
  const [optedIn, setOptedIn]   = useState(initialOptedIn ?? true)
  const [saving, setSaving]     = useState(false)
  const [error, setError]       = useState<string | null>(null)

  const phoneValid = /^\+258[0-9]{9}$/.test(phone.trim())

  const save = async () => {
    if (!phoneValid) {
      setError('Número inválido - usa o formato +2588XXXXXXXX (12 caracteres no total).')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await api.post('/auth/profile-contact', {
        phone_number:      phone.trim(),
        whatsapp_opted_in: optedIn,
      })
      onSaved()
    } catch (err: any) {
      setError(err?.data?.error ?? err?.message ?? 'Erro ao guardar')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Receber lembretes no WhatsApp</h3>
            <p className="text-xs text-gray-500 mt-0.5">Uma mensagem diária com 1 questão para te manteres consistente.</p>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-gray-500 text-2xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Número de telefone (Moçambique)</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+258841234567"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-400"
            />
            <p className="text-[10px] text-gray-400 mt-1">Formato: +258 seguido de 9 dígitos.</p>
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={optedIn}
              onChange={e => setOptedIn(e.target.checked)}
              className="mt-0.5"
            />
            <div className="text-xs text-gray-600">
              Aceito receber mensagens no WhatsApp do Tutor. Podes cancelar a qualquer momento no teu perfil.
            </div>
          </label>

          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1">{error}</p>}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2 bg-gray-50/50">
          <button
            onClick={onClose}
            disabled={saving}
            className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5"
          >
            Agora não
          </button>
          <button
            onClick={save}
            disabled={saving || !phoneValid}
            className="text-sm bg-indigo-600 text-white font-semibold px-5 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-40"
          >
            {saving ? 'A guardar…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  )
}
